import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCartStore } from "@/components/cart/store";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";
import { qrConfigurationQueryOptions, resolveQrTableByReference } from "@/lib/qr";
import { OrderTypes } from "@/lib/supabase/enums";
import { useQrSessionStore } from "@/stores/qr-session";

/**
 * Landing page for a QR code that is not tied to one table — a sticker that
 * was never assigned, or one taken off its table in the POS. The guest types
 * the number printed on their table and carries on to the menu.
 */
export const Route = createFileRoute("/$locale/qr/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(qrConfigurationQueryOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("translation");
  const { navigateWithLocale } = useLocaleNavigation();
  const configuration = useSuspenseQuery(qrConfigurationQueryOptions());

  const setSession = useQrSessionStore((state) => state.setSession);
  const setOrderType = useCartStore((state) => state.setOrderType);

  const [reference, setReference] = useState("");
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!configuration.data.enabled) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <h1 className="text-2xl font-bold">{t("Qr.disabled_title")}</h1>
        <p className="max-w-md text-gray-600">{t("Qr.disabled_description")}</p>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmed = reference.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    setError(undefined);

    try {
      const result = await resolveQrTableByReference({
        data: { reference: trimmed },
      });

      if (!result.success) {
        setError(
          result.code === "disabled"
            ? t("Qr.disabled_description")
            : t("Qr.unknown_table_description")
        );
        return;
      }

      setSession({
        tableId: result.table.id,
        tableReference: result.table.reference,
        sectionName: result.table.sectionName,
      });
      setOrderType(OrderTypes.pickup);
      navigateWithLocale("/menu", { replace: true });
    } catch {
      setError(t("Qr.general_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-5 rounded-xl border bg-white p-6 shadow"
      >
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold">{t("Qr.table_number_title")}</h1>
          <p className="text-gray-600">{t("Qr.table_number_description")}</p>
        </div>

        <input
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          // A number pad is what almost every table reference needs, but the
          // field stays text: references like "T12" or "Bar 3" are common.
          inputMode="text"
          autoFocus
          autoComplete="off"
          aria-label={t("Qr.table_number_title")}
          placeholder={t("Qr.table_number_placeholder")}
          className="min-h-12 w-full rounded-lg border px-4 text-center text-xl outline-none focus:border-black"
        />

        {error && (
          <p className="text-center text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!reference.trim() || isSubmitting}
          className="min-h-12 w-full cursor-pointer rounded-full bg-black/90 p-2 text-lg font-bold text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("Qr.table_number_submit")}
        </button>
      </form>
    </div>
  );
}
